/**
 * `canary workflow api-sequence <verb>` — manage the API sequence attached to an
 * api-only workflow.
 *
 * All verbs take a workflowId (not a raw apiSequenceId) to match the agent's
 * mental model: "the sequence attached to this workflow". The CLI resolves the
 * apiSequenceId via GET /api-sequences/by-workflow/:workflowId in one extra hop.
 *
 * Output auto-detects JSON (piped) vs text (TTY); --json/--text force either.
 *
 * @module WorkflowApiSequenceCmdsCLI
 */

import fs from 'node:fs/promises';
import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';
import { handleWorkflowGenerateApiSequence } from './workflow-api-sequence.js';

/* ── Arg helpers ───────────────────────────────────────────────────────── */

function getArg(argv: string[], key: string): string | undefined {
  const prefix = `${key}=`;
  const eq = argv.find((a) => a.startsWith(prefix));
  if (eq) return eq.slice(prefix.length);
  return getArgValue(argv, key);
}

function wantsJson(argv: string[]): boolean {
  if (hasFlag(argv, '--json')) return true;
  if (hasFlag(argv, '--text')) return false;
  return !process.stdout.isTTY;
}

function emit(argv: string[], textFallback: string, json: unknown): void {
  if (wantsJson(argv)) {
    console.log(JSON.stringify(json, null, 2));
  } else {
    console.log(textFallback);
  }
}

async function readJsonInput(argv: string[]): Promise<unknown> {
  const fromFile = getArg(argv, '--from-file');
  let raw: string;
  if (fromFile) {
    raw = await fs.readFile(fromFile, 'utf-8');
  } else if (hasFlag(argv, '--from-stdin') || !process.stdin.isTTY) {
    raw = await readStdin();
  } else {
    throw new Error('Provide --from-file <path> or pipe JSON on stdin.');
  }
  try {
    return JSON.parse(raw);
  } catch {
    throw new Error('Invalid JSON input.');
  }
}

function readStdin(): Promise<string> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    process.stdin.on('data', (chunk) => {
      chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
    });
    process.stdin.on('end', () => resolve(Buffer.concat(chunks).toString('utf-8')));
    process.stdin.on('error', reject);
  });
}

/* ── Types ─────────────────────────────────────────────────────────────── */

interface ApiSequenceStep {
  id?: string;
  endpointId?: string;
  method?: string;
  path?: string;
  [k: string]: unknown;
}

interface ApiSequenceConfig {
  steps: ApiSequenceStep[];
  description?: string;
  stopOnFailure?: boolean;
  exports?: unknown[];
}

interface ApiSequenceRecord {
  id: string;
  workflowId: string;
  orgId: string;
  credentialId: string | null;
  config: ApiSequenceConfig;
  status: string;
  guidancePrompt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ByWorkflowResponse {
  ok: boolean;
  error?: string;
  data?: ApiSequenceRecord | null;
}

interface ExecuteResponse {
  ok: boolean;
  error?: string;
  data?: { runId: string; status: string };
}

interface ValidationIssue {
  path: string;
  code: string;
  message: string;
  hint?: string;
}

interface GenericResponse {
  ok: boolean;
  error?: string;
  code?: string;
  issues?: ValidationIssue[];
  data?: unknown;
}

/**
 * Handle an error response from an api-sequence mutation. If the server
 * returned API_SEQUENCE_INVALID, emit a dense, machine-parseable report
 * so a coding agent can iterate: JSON mode emits `{ok:false, error, code,
 * issues}` to stdout; text mode writes `path [CODE] :: message` lines to
 * stderr. Always exits 1.
 *
 * Returns without exiting for non-validation errors so the caller can
 * fall back to its own error formatting.
 */
function reportAndMaybeExit(
  argv: string[],
  result: { ok: boolean; error?: string; code?: string; issues?: ValidationIssue[] },
): void {
  if (result.ok) return;

  if (result.code === 'API_SEQUENCE_INVALID' && Array.isArray(result.issues)) {
    if (wantsJson(argv)) {
      // stdout, so agents piping to jq get the full structured payload.
      console.log(
        JSON.stringify(
          {
            ok: false,
            error: result.error ?? 'API sequence configuration is invalid',
            code: 'API_SEQUENCE_INVALID',
            issueCount: result.issues.length,
            issues: result.issues,
          },
          null,
          2,
        ),
      );
    } else {
      console.error(`Error: ${result.error ?? 'API sequence configuration is invalid'}`);
      console.error(
        `Found ${result.issues.length} issue${result.issues.length === 1 ? '' : 's'}:`,
      );
      console.error('');
      // Dense form: one issue per line, `path [CODE] :: message` — grep/jq-friendly.
      for (const issue of result.issues) {
        console.error(`  ${issue.path || '(root)'} [${issue.code}] :: ${issue.message}`);
        if (issue.hint) console.error(`    hint: ${issue.hint}`);
      }
      console.error('');
      console.error(
        'Re-run with --json for machine-readable output, or fix the listed fields and retry.',
      );
    }
    process.exit(1);
  }

  // Non-validation error — caller-provided formatting.
  return;
}

/* ── Dispatcher ────────────────────────────────────────────────────────── */

export async function handleWorkflowApiSequence(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  const [verb, ...rest] = argv;
  switch (verb) {
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'update':
      await handleUpdate(rest, apiUrl, token);
      break;
    case 'execute':
    case 'run':
      await handleExecute(rest, apiUrl, token);
      break;
    case 'delete':
      await handleDelete(rest, apiUrl, token);
      break;
    case 'set-api-only':
      await handleSetApiOnly(rest, apiUrl, token);
      break;
    case 'generate':
      await handleWorkflowGenerateApiSequence(rest, apiUrl, token);
      break;
    default:
      printHelp();
      process.exit(verb ? 1 : 0);
  }
}

function printHelp() {
  console.error(
    [
      'Usage: canary workflow api-sequence <verb> <workflowId> [options]',
      '',
      'Verbs:',
      '  get <wf>                     fetch the sequence attached to the workflow',
      '  update <wf> --from-file <f>  replace config (steps, description, exports...)',
      '  update <wf> --from-stdin     replace config from piped JSON',
      '  execute <wf>                 run the sequence; returns { runId, status }',
      '  delete <wf>                  delete the api-sequence record (keeps workflow)',
      '  set-api-only <wf> [--off]    toggle the workflow\'s apiOnly flag',
      '  generate <wf> [--prompt ...] AI-generate a config (streams progress)',
      '',
      'Global options:',
      '  --json                       force JSON output',
      '  --text                       force pretty-text output (default when stdout is a TTY)',
      '',
      'Config shape (for update): { steps: [...], description?, stopOnFailure?, exports? }',
      'Run "canary workflow guide api-sequences" for step shape and template syntax.',
    ].join('\n'),
  );
}

/* ── Helpers ───────────────────────────────────────────────────────────── */

async function fetchSequenceByWorkflow(
  apiUrl: string,
  token: string,
  workflowId: string,
): Promise<ApiSequenceRecord> {
  const res = await apiRequest<ByWorkflowResponse>(
    apiUrl,
    token,
    'GET',
    `/api-sequences/by-workflow/${workflowId}`,
  );
  if (!res.ok) {
    console.error(`Error: ${res.error ?? 'Failed to fetch sequence'}`);
    process.exit(1);
  }
  if (!res.data) {
    console.error(`Error: Workflow ${workflowId} has no attached API sequence.`);
    console.error('Create one with: canary workflow create --api-only --name <n> --sequence-from-file seq.json');
    process.exit(1);
  }
  return res.data;
}

/* ── Handlers ──────────────────────────────────────────────────────────── */

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    printHelp();
    process.exit(1);
  }
  const seq = await fetchSequenceByWorkflow(apiUrl, token, workflowId);
  emit(argv, renderSequence(seq), seq);
}

async function handleUpdate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    printHelp();
    process.exit(1);
  }

  let parsed: unknown;
  try {
    parsed = await readJsonInput(argv);
  } catch (err) {
    console.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  const config = parsed as ApiSequenceConfig;
  if (!config || !Array.isArray(config.steps)) {
    console.error('Error: Input must have a "steps" array. Got: ' + JSON.stringify(config));
    process.exit(1);
  }

  const seq = await fetchSequenceByWorkflow(apiUrl, token, workflowId);

  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'PUT',
    `/api-sequences/${seq.id}/config`,
    { config },
  );

  reportAndMaybeExit(argv, result);
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Update failed'}`);
    process.exit(1);
  }

  emit(
    argv,
    `Updated api-sequence ${seq.id} (workflow ${workflowId}). Steps: ${config.steps.length}.`,
    result,
  );
}

async function handleExecute(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    printHelp();
    process.exit(1);
  }

  const seq = await fetchSequenceByWorkflow(apiUrl, token, workflowId);

  const result = await apiRequest<ExecuteResponse>(
    apiUrl,
    token,
    'POST',
    `/api-sequences/${seq.id}/execute`,
  );

  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'Execute failed'}`);
    process.exit(1);
  }

  emit(
    argv,
    `Run started: ${result.data.runId} (status: ${result.data.status})\n` +
      `Inspect: canary workflow runs get ${workflowId} ${result.data.runId}`,
    result,
  );
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    printHelp();
    process.exit(1);
  }

  const seq = await fetchSequenceByWorkflow(apiUrl, token, workflowId);

  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'DELETE',
    `/api-sequences/${seq.id}`,
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Delete failed'}`);
    process.exit(1);
  }

  emit(argv, `Deleted api-sequence ${seq.id} (workflow ${workflowId}).`, result);
}

async function handleSetApiOnly(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    printHelp();
    process.exit(1);
  }

  const apiOnly = !hasFlag(argv, '--off');

  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'PUT',
    `/workflows/${workflowId}/api-only`,
    { apiOnly },
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Update failed'}`);
    process.exit(1);
  }

  emit(argv, `Workflow ${workflowId} apiOnly=${apiOnly}.`, result);
}

/* ── Rendering ─────────────────────────────────────────────────────────── */

function renderSequence(seq: ApiSequenceRecord): string {
  const lines: string[] = [];
  lines.push(`API Sequence ${seq.id}`);
  lines.push(`  Workflow:    ${seq.workflowId}`);
  lines.push(`  Status:      ${seq.status}`);
  if (seq.credentialId) lines.push(`  Credential:  ${seq.credentialId}`);
  if (seq.config.description) lines.push(`  Description: ${seq.config.description}`);
  lines.push(`  stopOnFailure: ${seq.config.stopOnFailure ?? false}`);
  lines.push('');

  const steps = seq.config.steps ?? [];
  if (steps.length === 0) {
    lines.push('  (no steps — use `api-sequence update` to add)');
  } else {
    lines.push(`  Steps (${steps.length}):`);
    steps.forEach((step, i) => {
      const method = String(step.method ?? '??').toUpperCase().padEnd(6);
      const path = step.path ?? '(no path)';
      const id = step.id ? ` [${step.id}]` : '';
      lines.push(`    ${String(i + 1).padStart(2)}. ${method} ${path}${id}`);
      if (step.endpointId) lines.push(`        endpointId: ${step.endpointId}`);
    });
  }

  const exports = seq.config.exports ?? [];
  if (exports.length > 0) {
    lines.push('');
    lines.push(`  Exports: ${JSON.stringify(exports)}`);
  }

  return lines.join('\n');
}
